/**
 * Copy for "Same as…" in the Participants editor. Word for word the same as
 * the end-of-call review in both desktop apps — change all of them together.
 */
/**
 * The only session source on which "Same as…" is offered (owner decision
 * 2026-09-30). A standalone desktop call diarizes every remote voice from one
 * audio stream and can split one person into two ids; a bot call's speakers
 * come from Recall by meeting participant and never split. The backend refuses
 * `mergeInto` on any other source, so the editor does not show the control.
 */
export const MERGEABLE_SESSION_SOURCE = 'standalone_desktop';

/** Whether a session's rows may be merged ("Same as…"), from `session.metadata.source`. */
export const canMergeParticipants = (sessionSource: string | undefined | null): boolean =>
  sessionSource === MERGEABLE_SESSION_SOURCE;

export const PARTICIPANT_MERGE_COPY = {
  sameAs: 'Same as…',
  sameAsHeader: 'Same person as',
  undo: 'Undo',
  splitBack: 'Split back',
  namePlaceholder: 'Name',
} as const;

/** "1 turn" / "N turns". */
export const formatTurns = (turnCount: number): string => (turnCount === 1 ? '1 turn' : `${turnCount} turns`);

/** Collapsed merged row: "<name> · merged into <target> (<N> turn)" / "(<N> turns)". */
export const formatMergedRow = (name: string, targetName: string, turnCount: number): string =>
  `${name} · merged into ${targetName} (${formatTurns(turnCount)})`;

/** Accessible name of a merged row's Undo button. */
export const formatUndoLabel = (name: string, targetName: string): string =>
  `${PARTICIPANT_MERGE_COPY.undo}: ${name} is not the same person as ${targetName}`;

/** Accessible name of a "Same as…" option. */
export const formatSameAsLabel = (targetName: string): string =>
  `${PARTICIPANT_MERGE_COPY.sameAsHeader} ${targetName}`;

/** A participant's (already truncated) first line, in curly quotes. */
export const formatFirstLine = (firstLine: string): string => `“${firstLine}”`;

/** "1 line" / "N lines" (an automatic merge's size). */
export const formatLines = (lineCount: number): string => (lineCount === 1 ? '1 line' : `${lineCount} lines`);

/**
 * Collapsed automatic merge under its target row:
 * "“<first line>” (<N> line|lines) · merged automatically into <target>".
 * `firstLine` is already truncated; with no text the quote is left out.
 */
export const formatAutoMergedRow = (firstLine: string, lineCount: number, targetName: string): string =>
  `${firstLine ? `${formatFirstLine(firstLine)} ` : ''}(${formatLines(lineCount)}) \u00B7 merged automatically into ${targetName}`;

/** Accessible name of "Split back": "Split <first line> back out of <target>". */
export const formatSplitBackLabel = (firstLine: string, targetName: string): string =>
  `Split ${firstLine} back out of ${targetName}`;
